import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { KickstartService } from '../../../core/kickstart/kickstart.service';
import {
  KsApplicationDetail,
  KsFirstLook,
  KsOutstandingItem,
  KsStageRow,
  KsPosition,
  KsTaxPin,
} from '../../../core/kickstart/kickstart.types';
import { DfService } from '../../../core/df/df.service';
import { DfFacilityDetail } from '../../../core/df/df.types';
import { IconComponent } from '../../../shared/icon/icon';
import { ContextBlocksClientComponent } from '../../entity/shared/context-blocks-client/context-blocks-client';
import { ZarPipe } from '../../../shared/pipes/zar.pipe';
import { DashPipe } from '../../../shared/pipes/dash.pipe';
import { StatusChipComponent } from '../../../shared/status-chip/status-chip';
import { KsOutstandingComponent } from '../../entity/kickstart/detail/outstanding/ks-outstanding';
import { agentProgressLine } from './progress';
import { StageRowComponent } from '../../../shared/stage-row/stage-row';
import { FirstLookComponent, firstLookApplies } from '../../../shared/first-look/first-look';

/** Product slug carried in the route — keeps the screen KS/DF-agnostic. */
export type AgentAppProduct = 'ks' | 'df';

/**
 * The application's progress, normalised across KS and DF so the template can
 * render the same stepper, status chips and facts for either product.
 *
 * Deliberately AI-free: the agent persona never sees AI scores (N4), so this
 * screen reads only from the client-facing endpoints.
 */
interface NormalisedApp {
  readonly product: AgentAppProduct;
  readonly shortId: string;
  /** Canonical product code for the chip — 'KS' | 'DF'. */
  readonly productCode: 'KS' | 'DF';
  readonly productLabel: string;
  readonly entityName: string;
  readonly entityShortId: string;
  /** ZAR decimal string, or null when not yet captured (DF requested limit). */
  readonly amount: string | null;
  readonly facilityStatus: string;
  readonly applicationStage: string;
  /** FLOW-402 (DOC-89-A §5) — the stage row from the record; none on DF. */
  readonly stageRow: KsStageRow | null;
  readonly submittedAt: string | null;
  readonly detail: string | null;
  readonly detailLabel: string;
  readonly documentNames: string[];
  /** FLOW-344 — Kickstart's outstanding list and position (DOC-73); none on DF. */
  readonly outstanding: readonly KsOutstandingItem[];
  readonly position: KsPosition | null;
  /** FLOW-352 (DOC-83) — the tax compliance PINs sent; none on DF. */
  readonly taxPins: readonly KsTaxPin[];
  readonly taxPinOpen: boolean;
  /** FLOW-224 (DOC-78) — Kickstart only: the agent sees what the client sees. */
  readonly showsFirstLook: boolean;
  readonly firstLook: KsFirstLook | null | undefined;
  readonly offerIssued: boolean;
  /** Deep-link into the client workspace for this application. */
  readonly workspaceLink: readonly (string | number)[];
}

/**
 * Read-only "Application progress" screen for the agent persona. Shows how a
 * single in-flight KS / DF application is moving through the journey, derived
 * from the client-facing status fields — no AI data, no decision controls.
 */
@Component({
  selector: 'app-agent-application-detail',
  imports: [
    RouterLink,
    DatePipe,
    MatButtonModule,
    IconComponent,
    ContextBlocksClientComponent,
    ZarPipe,
    DashPipe,
    StatusChipComponent,
    StageRowComponent,
    KsOutstandingComponent,
    FirstLookComponent,
  ],
  templateUrl: './agent-application-detail.html',
  styleUrl: './agent-application-detail.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentApplicationDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly ksService = inject(KickstartService);
  private readonly dfService = inject(DfService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly app = signal<NormalisedApp | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);

  private product: AgentAppProduct = 'ks';
  private appId = '';

  /** A plain-language line telling the agent what happens next. */
  protected readonly nextStep = computed<string>(() => {
    const a = this.app();
    if (!a) return '';
    return agentProgressLine(a.applicationStage, a.facilityStatus, a.position, a.outstanding);
  });

  ngOnInit(): void {
    this.product = this.route.snapshot.paramMap.get('product') === 'df' ? 'df' : 'ks';
    this.appId = this.route.snapshot.paramMap.get('appId') ?? '';
    this.load();
  }

  /** `quiet` re-reads without the skeleton — after an act on an outstanding item. */
  protected load(quiet = false): void {
    if (!quiet) this.isLoading.set(true);
    this.loadError.set(false);

    if (this.product === 'df') {
      this.dfService
        .getFacility(this.appId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (f) => this.onLoaded(normaliseDf(f)),
          error: () => this.onError(),
        });
    } else {
      this.ksService
        .get(this.appId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (k) => this.onLoaded(normaliseKs(k)),
          error: () => this.onError(),
        });
    }
  }

  private onLoaded(app: NormalisedApp): void {
    this.app.set(app);
    this.isLoading.set(false);
    this.cdr.markForCheck();
  }

  private onError(): void {
    this.isLoading.set(false);
    this.loadError.set(true);
    this.cdr.markForCheck();
  }
}

// ---------------------------------------------------------------------------
// Pure helpers (unit-tested) — normalisation + journey derivation
// ---------------------------------------------------------------------------

function normaliseKs(k: KsApplicationDetail): NormalisedApp {
  return {
    product: 'ks',
    shortId: k.short_id,
    productCode: 'KS',
    productLabel: 'Kickstart Cash',
    entityName: k.entity_name,
    entityShortId: k.entity_short_id,
    amount: k.amount_requested,
    facilityStatus: k.ks_status,
    applicationStage: k.application_stage,
    stageRow: k.stage_row,
    submittedAt: k.submitted_at,
    detail: k.purpose,
    detailLabel: 'Purpose',
    documentNames: k.documents.map((d) => d.file_name),
    outstanding: k.outstanding ?? [],
    position: k.position ?? null,
    taxPins: k.tax_pins ?? [],
    taxPinOpen: k.tax_pin_open ?? false,
    showsFirstLook: firstLookApplies(k.application_stage),
    firstLook: k.first_look,
    offerIssued: !!k.offer,
    workspaceLink: ['/entity', k.entity_short_id, 'kickstart', k.short_id],
  };
}

function normaliseDf(f: DfFacilityDetail): NormalisedApp {
  const subtype = f.facility_subtype
    ? f.facility_subtype.charAt(0) + f.facility_subtype.slice(1).toLowerCase()
    : null;
  return {
    product: 'df',
    shortId: f.short_id,
    productCode: 'DF',
    productLabel: 'Debtors Finance',
    entityName: f.entity_name,
    entityShortId: f.entity,
    amount: f.requested_limit,
    facilityStatus: f.df_status,
    applicationStage: f.application_stage,
    // The stage row is Kickstart's (DOC-89-A §5); Debtors Finance carries none.
    stageRow: null,
    submittedAt: f.submitted_at,
    detail: subtype,
    detailLabel: 'Facility type',
    documentNames: f.documents.map((d) => d.file_name),
    outstanding: [],
    position: null,
    taxPins: [],
    taxPinOpen: false,
    // §A12.4: whether a Debtors Finance figure may be published is undetermined.
    showsFirstLook: false,
    firstLook: null,
    offerIssued: false,
    workspaceLink: ['/entity', f.entity, 'df', f.short_id],
  };
}
